/**
 * Read-only summary tile for catalog fields marked with
 * `field.computed`. Renders the current sum (formatted as USD) with
 * a small "auto-calculated" badge so users understand they don't
 * need to type it themselves. The underlying engine value is kept
 * in sync by `useComputedTotalSync` (see computedTotals.ts).
 */
import { useEffect, useState } from 'react';
import type { FieldDefinition } from '@inside-us-aos/domain';
import type { FormEngine } from '@inside-us-aos/form-engine';

import styles from './ComputedTotalTile.module.css';

export interface IComputedTotalTileProps {
  field: FieldDefinition;
  engine: FormEngine;
}

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function readNumber(engine: FormEngine, field: FieldDefinition): number {
  const ans = engine.getAnswer(field.id);
  if (ans?.type === 'number' && Number.isFinite(ans.value)) return ans.value;
  return 0;
}

export function ComputedTotalTile(
  props: IComputedTotalTileProps,
): JSX.Element {
  const { field, engine } = props;
  const [value, setValue] = useState<number>(() => readNumber(engine, field));
  useEffect(
    () => engine.subscribe(() => setValue(readNumber(engine, field))),
    [engine, field],
  );

  return (
    <div
      className={styles.tile}
      data-computed-total={field.id}
      aria-label={`${field.label}: ${USD_FORMATTER.format(value)} (auto-calculated)`}
      role="status"
    >
      <div className={styles.tile__head}>
        <span className={styles.tile__label}>{field.label}</span>
        <span className={styles.tile__badge} aria-hidden="true">
          auto-calculated
        </span>
      </div>
      <div className={styles.tile__value}>{USD_FORMATTER.format(value)}</div>
    </div>
  );
}
